// DEPRECATED

/*
 * Scrollable tabs
 *
 * Usage:
 *
 *      <Tabs
 *          selectedTabClassName=''         // className to be applied to selected tab
 *          selectedTabStyle={{ ... }}      // style to be applied to selected tab
 *          minTabWidth={100}               // if resulting tab width is smaller than this, scroll will be enabled
 *          >
 *          <button type="button" selected>Tab 1</button>
 *          <button type="button">Tab 2</button>
 *          <button type="button"> ... </button>
 *          <button type="button">Tab N</button>
 *      </Tabs>
 */

import _, { defer } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Tabs.scss';

const Tabs = (props) => {
    const {
            className,
            selectedTabClassName = '',
            selectedTabStyle = {},
            minTabWidth,
            children,
        } = props,
        [containerWidth, setContainerWidth] = useState(window.innerWidth),
        [leftPosition, setLeftPosition] = useState(0),
        [visibleTabs, setVisibleTabs] = useState(Number.POSITIVE_INFINITY),
        [centered, setCentered] = useState(true),
        [dragging, setDragging] = useState(false),
        [ready, setReady] = useState(false),
        tabs = _(React.Children.toArray(children))
            .flattenDeep()
            .compact()
            .value(),
        ref = useRef(),
        tabWidth = containerWidth / visibleTabs,
        showLeftArrow = leftPosition > 0,
        showRightArrow = leftPosition + visibleTabs < tabs.length;

    let selectedTab = tabs.findIndex((tab) => tab?.props.selected);
    if (selectedTab < 0) selectedTab = 0;

    // Container size
    useEffect(() => {
        if (!ref.current) return;

        function updateContainerWidth() {
            setContainerWidth(ref.current.querySelector('.tabs').offsetWidth);
        }
        updateContainerWidth();
        window.addEventListener('resize', updateContainerWidth);

        return () => window.removeEventListener('resize', updateContainerWidth);
    });

    // Check tabs width
    useEffect(() => {
        if (containerWidth) {
            let possibleTabs = tabs.length;

            // How many tabs fit on the container
            while (
                containerWidth / possibleTabs < minTabWidth &&
                possibleTabs > 0
            ) {
                possibleTabs -= 1;
            }
            if (possibleTabs < 1) {
                possibleTabs = 1;
            }
            if (
                possibleTabs < visibleTabs ||
                Math.abs(possibleTabs - visibleTabs) > 1
            ) {
                setVisibleTabs(possibleTabs);
            }
        }
    }, [tabs, minTabWidth, containerWidth]);

    // Center tab
    useEffect(() => {
        if (!dragging && tabWidth && containerWidth) {
            if (centered) {
                let visibleTabs = containerWidth / tabWidth,
                    leftPosition = selectedTab - (visibleTabs - 1) / 2;
                if (leftPosition + visibleTabs > tabs.length) {
                    leftPosition = tabs.length - visibleTabs;
                }
                if (leftPosition < 0) {
                    leftPosition = 0;
                }
                setLeftPosition(Math.floor(leftPosition));
            }
        }
    }, [selectedTab, centered, tabWidth, dragging]);

    // Above and below limits
    useEffect(() => {
        let newLeftPosition = leftPosition;
        if (newLeftPosition + visibleTabs >= tabs.length)
            newLeftPosition = tabs.length - visibleTabs;
        if (newLeftPosition < 0) newLeftPosition = 0;
        if (newLeftPosition != leftPosition) setLeftPosition(newLeftPosition);
    }, [leftPosition, visibleTabs]);

    useEffect(() => {
        if (!ready && (tabWidth >= minTabWidth || visibleTabs == 1)) {
            setReady(true);
        }
    }, [tabWidth]);

    const handleLeftButtonClicked = useCallback(() => {
        let lp = leftPosition - 1;
        setCentered(false);
        setLeftPosition(lp);
    }, [leftPosition]);

    const handleRightButtonClicked = useCallback(() => {
        let lp = leftPosition + 1;
        setCentered(false);
        setLeftPosition(lp);
    }, [leftPosition, visibleTabs, tabs]);

    const handleStartDragging = useCallback(
        (e) => {
            let lastTouchX = (e.touches[0] || e).clientX,
                scrollLeft = -1 * leftPosition * tabWidth;

            const handleDrag = (e) => {
                let x = (e.touches[0] || e).clientX,
                    diff = x - lastTouchX;

                if (diff) {
                    let left = scrollLeft + diff;
                    if (
                        -left + visibleTabs * tabWidth >
                        tabs.length * tabWidth + 20
                    ) {
                        left = -1 * (tabs.length - visibleTabs) * tabWidth - 20;
                    }
                    if (left > 20) {
                        left = 20;
                    }
                    lastTouchX = x;
                    scrollLeft = left;
                    setDragging({ scrollLeft: left });
                }
            };

            const handleEndDragging = () => {
                let leftPosition = _(tabs)
                    .map((tab, i) => i)
                    .sortBy((i) => Math.abs(scrollLeft - -1 * i * tabWidth))
                    .first();
                setDragging(null);
                setCentered(false);
                setLeftPosition(leftPosition);
            };

            let stopListeners = () => {
                window.removeEventListener('touchmove', handleDrag);
                defer(handleEndDragging);
            };
            window.addEventListener('touchmove', handleDrag);
            window.addEventListener('touchend', stopListeners, { once: true });

            setDragging({
                scrollLeft,
            });
        },
        [leftPosition, tabWidth, tabs]
    );

    const handleTabClicked = useCallback((event, tab) => {
        if (!dragging) {
            if (tab.props.onClick) {
                tab.props.onClick(event);
            }
            setCentered(true);
        }
    }, []);

    return (
        <div
            ref={ref}
            className={[
                'Tabs',
                className,
                showLeftArrow ? 'hasLeftArrow' : '',
                showRightArrow ? 'hasRightArrow' : '',
                ready ? 'ready' : '',
            ].join(' ')}>
            {showLeftArrow ? (
                <button
                    type='button'
                    className='leftArrow'
                    onClick={handleLeftButtonClicked}
                />
            ) : null}
            <div className='tabs' onTouchStart={handleStartDragging}>
                <div
                    className='scroller'
                    style={{
                        left: dragging
                            ? dragging.scrollLeft
                            : -1 * leftPosition * tabWidth,
                        width: tabs.length * tabWidth,
                    }}>
                    {tabs.map((tab, i) =>
                        React.cloneElement(
                            tab,
                            {
                                className: [
                                    tab.props.className ?? '',
                                    'tab',
                                    tab.props.selected ? 'selected' : '',
                                    tab.props.selected
                                        ? selectedTabClassName
                                        : '',
                                ].join(' '),
                                onClick: (event) =>
                                    handleTabClicked(event, tab),
                                style: {
                                    ...tab.props.style,
                                    width: tabWidth,
                                    ...(tab.props.selected
                                        ? selectedTabStyle
                                        : {}),
                                },
                            },
                            <span>{tab.props.children}</span>
                        )
                    )}
                </div>
            </div>
            {showRightArrow ? (
                <button
                    type='button'
                    className='rightArrow'
                    onClick={handleRightButtonClicked}
                />
            ) : null}
        </div>
    );
};

export default Tabs;
